import { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { ArrowRight, Zap, Sparkles } from "lucide-react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";
import TextScramble from "@/components/TextScramble";
import { SkeletonHero } from "@/components/SkeletonShimmer";
import { fetchPage, getSection } from "@/lib/api";

gsap.registerPlugin(ScrollTrigger);

/* ── Floating "AI Engine" card that scales on scroll ── */
// function EngineCard({ progress }) {
//   return (
//     <div
//       className="absolute inset-0 flex items-center justify-center pointer-events-none"
//       style={{ zIndex: 0 }}
//     >
//       <motion.div
//         className="w-[340px] h-[220px] md:w-[500px] md:h-[320px] rounded-3xl border border-indigo-500/20 bg-white/[0.03] backdrop-blur-2xl shadow-2xl shadow-indigo-500/10 overflow-hidden"
//         style={{
//           scale: 0.6 + progress * 0.8,
//           opacity: 0.3 + progress * 0.7,
//         }}
//       >
//         {/* Fake dashboard content inside */}
//         <div className="h-full w-full p-6 flex flex-col justify-between">
//           <div className="flex items-center gap-2">
//             <div className="w-3 h-3 rounded-full bg-red-400/60" />
//             <div className="w-3 h-3 rounded-full bg-yellow-400/60" />
//             <div className="w-3 h-3 rounded-full bg-green-400/60" />
//             <span className="ml-auto text-[10px] text-white/30 font-mono">
//               brgt.ai engine v3
//             </span>
//           </div>
//           <div className="flex-1 mt-4 space-y-2">
//             <div className="h-2 rounded bg-indigo-500/20 w-3/4" />
//             <div className="h-2 rounded bg-indigo-500/15 w-1/2" />
//             <div className="h-2 rounded bg-indigo-500/10 w-2/3" />
//             <div className="mt-4 grid grid-cols-3 gap-2">
//               {[...Array(6)].map((_, i) => (
//                 <div
//                   key={i}
//                   className="h-8 rounded-lg bg-white/[0.03] border border-white/5"
//                 />
//               ))}
//             </div>
//           </div>
//           <div className="flex items-center gap-2">
//             <Sparkles size={14} className="text-indigo-400/60" />
//             <span className="text-[10px] text-white/30 font-mono">
//               processing…
//             </span>
//           </div>
//         </div>
//       </motion.div>
//     </div>
//   );
// }

/* ── Main Hero component ── */
export default function Hero() {
  const containerRef = useRef(null);
  const textRef = useRef(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const isInView = useInView(containerRef, { once: true });

  // Fetch CMS data
  useEffect(() => {
    fetchPage("home")
      .then(setPageData)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // GSAP pinned scroll — text fades out, card scales up
  useGSAP(
    () => {
      const prefersReduced = window.matchMedia(
        "(prefers-reduced-motion: reduce)"
      ).matches;
      if (prefersReduced) return;

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: containerRef.current,
          start: "top top",
          end: "+=100%",
          pin: true,
          scrub: 0.8,
          onUpdate: (self) => setScrollProgress(self.progress),
        },
      });

      // Fade out text layer
      tl.to(textRef.current, {
        opacity: 0,
        y: -60,
        duration: 1,
        ease: "power2.in",
      });
    },
    { scope: containerRef }
  );

  const headline = getSection(
    pageData,
    "hero_headline",
    "AI That Fits Your Business — Not the Other Way Around."
  );
  const sub = getSection(
    pageData,
    "hero_subheadline",
    "Our team brings deep domain expertise across pharmaceuticals, medical devices, and industrial manufacturing \u2014 translating specialized knowledge into purpose-built Agentic and Generative AI systems that deliver measurable outcomes for your business."
  );
  const cta = getSection(pageData, "hero_cta", "Get Started");

  return (
    <section
      ref={containerRef}
      className="relative min-h-screen flex items-center overflow-hidden"
      data-testid="hero-section"
    >
      {/* Background gradients */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-indigo-600/8 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-violet-500/6 rounded-full blur-[100px]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-indigo-500/4 rounded-full blur-[150px]" />
      </div>

      {/* AI Engine card — scales with scroll */}
      {/* <EngineCard progress={scrollProgress} /> */}

      {/* Text layer — fades out on scroll */}
      <div
        ref={textRef}
        className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full"
      >
        {loading ? (
          <SkeletonHero />
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            {/* Badge */}
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.1, duration: 0.5 }}
              className="inline-flex items-center gap-2 bg-indigo-500/10 text-indigo-300 border border-indigo-500/20 rounded-full px-4 py-1.5 text-xs font-semibold tracking-wider uppercase mb-8"
            >
              <Zap size={14} className="text-indigo-400" /> AI Solutions for
              SMEs
            </motion.span>

            {/* Headline — fluid clamp typography + scramble */}
            <h1
              className="font-heading font-bold text-white leading-[1.05] mb-6"
              style={{
                fontSize: "clamp(2.2rem, 5vw + 1rem, 4.5rem)",
              }}
            >
              <TextScramble text={headline} trigger={isInView} speed={25} />
            </h1>

            {/* Subheadline */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.4, duration: 0.6 }}
              className="text-base md:text-lg text-slate-400 leading-relaxed max-w-xl mb-10"
            >
              {sub}
            </motion.p>

            {/* CTAs */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.6, duration: 0.5 }}
              className="flex flex-wrap gap-4"
            >
              <Link
                to="/contact"
                data-testid="hero-cta-primary"
                data-magnetic
                className="group inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-full px-8 py-3.5 font-semibold transition-all shadow-lg shadow-indigo-600/25 active:scale-95"
              >
                {cta}
                <ArrowRight
                  size={18}
                  className="transition-transform group-hover:translate-x-1"
                />
              </Link>
              <Link
                to="/solutions"
                data-testid="hero-cta-secondary"
                data-magnetic
                className="inline-flex items-center gap-2 bg-white/5 text-white border border-white/10 hover:bg-white/10 rounded-full px-8 py-3.5 font-semibold transition-all backdrop-blur-sm active:scale-95"
              >
                Explore Solutions
              </Link>
            </motion.div>
          </motion.div>
        )}
      </div>
    </section>
  );
}
